import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility'; // Added for View button
import DeleteIcon from '@mui/icons-material/Delete'; // Added for View button
import { AppBar, Avatar, IconButton, Box, Typography, Card, CardContent, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import WorkPlaceBox from '../WorkPlaceBox';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useTranslation } from 'react-i18next';

export default function ProfileSection({ handleChangeBrand, handleInitCall }) {
  const theme = useTheme();
  const { t: translate } = useTranslation();

  const initialLoad = CommonHelper.GetBrandInfoFromLocalStorage();
  const activeBrandId = CommonHelper.GetCurrentBrandId();
  const [brandId, setBrandId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isOrgUser = CommonHelper.GetBrandInfoFromLocalStorage('is_org_user');
  const isReseller = CommonHelper.GetBrandInfoFromLocalStorage('is_reseller');
  const isOrgAdminHasPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.ORG_ADMIN
  );
  const navigate = useNavigate();

  const [openWorkPlaceModal, setWorkPlaceModal] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const [currentBrandId, setCurrentBrand] = useState(activeBrandId);
  const [pinnedBrands, setPinnedBrands] = useState([]);

  const { showToast } = useToaster();

  useEffect(() => {
    getAllBrandsFromLocalStorage();
    getPinnedBrandsFromLocalStorage();
  }, []);

  const getAllBrandsFromLocalStorage = () => {
    let brand_details = initialLoad?.brand_details;
    if (brand_details?.length > 0) {
      setResponseData(brand_details);
    }
  };

  const getPinnedBrandsFromLocalStorage = () => {
    const stored = localStorage.getItem('pinnedBrands');
    if (stored) {
      try {
        setPinnedBrands(JSON.parse(stored));
      } catch (error) {
        console.error('Error parsing pinned brands from localStorage:', error);
        setPinnedBrands([]);
      }
    }
  };

  const savePinnedBrandsToLocalStorage = (pinnedBrandIds) => {
    localStorage.setItem('pinnedBrands', JSON.stringify(pinnedBrandIds));
  };

  const handlePinToggle = (e, brandId) => {
    e.stopPropagation(); // Prevent brand selection when clicking pin

    let updatedPinnedBrands;
    if (pinnedBrands.includes(brandId)) {
      // Unpin
      updatedPinnedBrands = pinnedBrands.filter((id) => id !== brandId);
      showToast('Brand unpinned', 's');
    } else {
      // Pin
      updatedPinnedBrands = [...pinnedBrands, brandId];
      showToast('Brand pinned', 's');
    }

    setPinnedBrands(updatedPinnedBrands);
    savePinnedBrandsToLocalStorage(updatedPinnedBrands);
  };

  // Sort brands to show pinned ones first
  const sortedBrands = responseData.sort((a, b) => {
    const aIsPinned = pinnedBrands.includes(a.brand_id);
    const bIsPinned = pinnedBrands.includes(b.brand_id);

    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;
    return 0;
  });

  const confirmDelete = () => {
    setConfirmDeleteOpen(false);
    let brandprops = { brand_id: brandId };
    let postProps = {
      url: userModuleURL.deleteBrand,
      body: brandprops,
      successCallback: updateSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const updateSuccessCallback = (data, message) => {
    window.location.reload();
  };

  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const handleListItemClick = (brandId) => {
    setCurrentBrand(brandId);
    handleChangeBrand(brandId);
  };

  const handleWorkPlaceClick = () => {
    setWorkPlaceModal(true);
  };

  const handleClose = () => {
    setWorkPlaceModal(false);
  };

  const handleSuccess = () => {
    handleClose();
    handleInitCall();
  };

  const workPlaceProps = { handleClose, handleSuccess };

  return (
    <>
      <AppBar
        sx={{
          position: 'relative',
          left: 0,
          height: '100vh',
          width: theme.spacing(9),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          backgroundColor: 'white',
          padding: '10px !important',
          borderRight: `2px dashed ${theme.palette.secondary.main}`,
        }}
      >
        <div
          style={{
            height: '97vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              overflow: 'auto',
              flex: 1,
              alignItems: 'center',
              paddingTop: '0.5rem',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {sortedBrands.map((brand, index) => (
              <CommonHelper.HtmlTooltip
                key={brand.brand_id}
          title={
                  <Card sx={{ maxWidth: 320, borderRadius: 2, boxShadow: 4 }}>
                    <CardContent sx={{ alignItems: 'center', padding: 2 }}>
                      {/* Top Section: Avatar + Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {' '}
                        {/* Changed to 'center' */}
                        <Avatar
                          alt={brand.brand_name}
                          src={brand.brand_image || ''}
                          sx={{
                            width: 64,
                            height: 64,
                            mr: 2,
                           color: theme.palette.primary.main,
                            bgcolor: theme.palette.secondary.main,
                              border:
                              currentBrandId === brand.brand_id
                                ? `2.5px solid ${theme.palette.primary.main}`
                                : `1.5px solid white`,
                          }}
                          variant="rounded"
                        >
                          {!brand.brand_image && brand.brand_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {brand.brand_name}
                        </Typography>
                      </Box>

                      {/* Bottom Section: Buttons */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1.5,
                          justifyContent: 'center',
                          alignItems: 'center',
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          size="medium"
                          startIcon={<VisibilityIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/manage-brand');
                          }}
                          sx={{ minWidth: 110 }}
                        >
                          {translate('preview.view')}
                        </Button>

                        {isOrgUser && !isReseller && isOrgAdminHasPermission && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="medium"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setBrandId(brand.brand_id);
                              setConfirmDeleteOpen(true);
                            }}
                            sx={{ minWidth: 110 }}
                          >
                            {translate('common.delete')}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                }
                placement="right"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport',
                        padding: 8,
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['left', 'top', 'bottom'],
                      },
                    },
                  ],
                }}
                enterDelay={300}
                leaveDelay={200}
              >
                <div style={{ position: 'relative' }} onClick={() => handleListItemClick(brand.brand_id)}>
                  <Avatar
                    src={brand.brand_image ? brand.brand_image : brand.brand_name}
                    alt={brand.brand_name}
                    sx={{
                      textTransform: 'capitalize',
                      cursor: 'pointer',
                      color: `${theme.palette.primary.main}`,
                      backgroundColor: `${theme.palette.secondary.main}`,
                      boxSizing: 'content-box',
                      border:
                        currentBrandId === brand.brand_id
                          ? `2px solid ${theme.palette.primary.main}`
                          : `2px solid rgb(255, 255, 255)`,
                      ...(sortedBrands.length - 1 === index && {
                        marginBottom: '1rem',
                      }),
                    }}
                    variant="rounded"
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handlePinToggle(e, brand.brand_id)}
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      backgroundColor: 'white',
                      border: '1px solid #e0e0e0',
                      width: 16,
                      height: 16,
                      zIndex: 10,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      minWidth: 'unset',
                      padding: 0,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    {pinnedBrands.includes(brand.brand_id) ? (
                      <PushPinIcon sx={{ fontSize: 10, color: theme.palette.primary.main }} />
                    ) : (
                      <PushPinOutlinedIcon sx={{ fontSize: 10, color: '#666' }} />
                    )}
                  </IconButton>
                </div>
              </CommonHelper.HtmlTooltip>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingBottom: '1rem',
            }}
          >
            {CommonHelper.CheckForPermissionAvailableForUser() && (
              <CommonHelper.HtmlTooltip
                title="Add New Brand"
                placement="right"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                }}
                sx={{
                  '& .MuiTooltip-tooltip': {
                    color: 'black',
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                    padding: '4px 8px',
                  },
                  '& .MuiTooltip-arrow': {
                    color: 'white',
                  },
                }}
              >
                <Avatar
                  onClick={handleWorkPlaceClick}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    cursor: 'pointer',
                    boxSizing: 'content-box',
                    border: '2px solid rgb(255, 255, 255)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                  variant="rounded"
                >
                  <AddIcon />
                </Avatar>
              </CommonHelper.HtmlTooltip>
            )}
          </div>
        </div>
      </AppBar>
      <ConfirmationDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={translate('common.confirm_deletion')}
        message={`${translate('common.confirm_delete_brand')}?`}
      />
      {openWorkPlaceModal && <WorkPlaceBox {...workPlaceProps} />}
    </>
  );
}
